import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, AtSign } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import AdminCard from "./AdminCard";

export default function KontoAdmin({ user, onUserUpdate }) {
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
    const [loading, setLoading] = useState(false);

    const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
    const [emailLoading, setEmailLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (form.new_password !== form.confirm_password) {
            toast.error("Nowe hasła nie są identyczne.");
            return;
        }
        if (form.new_password.length < 6) {
            toast.error("Hasło musi mieć co najmniej 6 znaków.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            toast.success("Hasło zostało zmienione.");
            setForm({ current_password: "", new_password: "", confirm_password: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const submitEmail = async (e) => {
        e.preventDefault();
        if (!emailForm.new_email || !emailForm.current_password) {
            toast.error("Wypełnij oba pola.");
            return;
        }
        setEmailLoading(true);
        try {
            const r = await api.post("/auth/change-email", emailForm);
            toast.success(r.data?.message || "Adres e-mail zmieniony. Zaloguj się ponownie.");
            setEmailForm({ new_email: "", current_password: "" });
            // refresh local user info if provided
            if (onUserUpdate) onUserUpdate({ ...user, email: r.data?.email || emailForm.new_email });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setEmailLoading(false); }
    };

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Dane konta</h3>
                <div className="divider-gold w-12 mb-5" />
                <dl className="space-y-3 text-sm" data-testid="admin-account-info">
                    <div className="flex justify-between">
                        <dt className="text-gray-500">E-mail</dt>
                        <dd className="font-medium text-[#092E22]">{user?.email}</dd>
                    </div>
                    <div className="flex justify-between">
                        <dt className="text-gray-500">Rola</dt>
                        <dd>
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${user?.role === "admin" ? "bg-[#D4A537]/20 text-[#B88A26]" : "bg-blue-100 text-blue-800"}`}>
                                <ShieldCheck size={11} /> {user?.role === "admin" ? "Administrator" : "Moderator"}
                            </span>
                        </dd>
                    </div>
                    {user?.is_main && (
                        <div className="text-xs text-[#0D4F3C] bg-emerald-50 border border-emerald-200 rounded-xl p-3 mt-3">
                            To jest konto głównego administratora. Nie można go usunąć.
                        </div>
                    )}
                </dl>
            </AdminCard>

            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Zmiana hasła</h3>
                <p className="text-sm text-gray-500 mb-5">Wpisz aktualne hasło i nowe hasło (min. 6 znaków).</p>
                <form onSubmit={submit} className="space-y-4" data-testid="admin-change-password-form">
                    <div className="space-y-2">
                        <Label>Aktualne hasło</Label>
                        <Input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="admin-current-password" />
                    </div>
                    <div className="space-y-2">
                        <Label>Nowe hasło</Label>
                        <Input type="password" required minLength={6} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="admin-new-password" />
                    </div>
                    <div className="space-y-2">
                        <Label>Powtórz nowe hasło</Label>
                        <Input type="password" required minLength={6} value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} data-testid="admin-confirm-password" />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-change-password-submit" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        <KeyRound size={14} className="mr-2" /> {loading ? "Zmienianie..." : "Zmień hasło"}
                    </Button>
                </form>
            </AdminCard>

            <AdminCard className="lg:col-span-2">
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Zmiana adresu e-mail</h3>
                <p className="text-sm text-gray-500 mb-5">
                    Nowy adres będzie służył do logowania. Wymagamy podania aktualnego hasła dla bezpieczeństwa.
                </p>
                <form onSubmit={submitEmail} className="space-y-4 max-w-xl" data-testid="admin-change-email-form">
                    <div className="space-y-2">
                        <Label>Nowy adres e-mail</Label>
                        <Input
                            type="email"
                            required
                            value={emailForm.new_email}
                            onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                            placeholder="nowy@adres.pl"
                            data-testid="admin-new-email"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Aktualne hasło</Label>
                        <Input
                            type="password"
                            required
                            value={emailForm.current_password}
                            onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
                            data-testid="admin-email-current-password"
                        />
                    </div>
                    <Button type="submit" disabled={emailLoading} data-testid="admin-change-email-submit" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        <AtSign size={14} className="mr-2" /> {emailLoading ? "Zmienianie..." : "Zmień adres e-mail"}
                    </Button>
                </form>
            </AdminCard>
        </div>
    );
}
